import { useEffect, useMemo, useState } from "react";

import {
	useAddressBookItems,
	useAddressBookItemsFiltered,
} from "~/features/address-book/hooks/use-address-book.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import type {
	AddressBookFilteredQueryParams,
	AddressBookItemSummary,
	AddressBookQueryParams,
} from "~/shared/schemas/address-book-schema.ts";
import type { SubGroupKey } from "~/shared/types/quote-details-types.ts";

export type AddressBookLocationFilter = {
	postalCode?: string;
	city?: string;
	state?: string;
	country?: string;
};

type UseAddressBookDataProps = {
	showModal: boolean;
	/** Customer whose address book is being browsed. */
	customerId: number;
	/** When true, filters address book items by the provided `locationFilter`. */
	useLocationFilter?: boolean;
	/** The address type - "pickup" or "delivery" - used to build the stop type. */
	addressType?: SubGroupKey;
	/** Location used to filter the address book when `useLocationFilter` is set. */
	locationFilter?: AddressBookLocationFilter;
};

export function useAddressBookData({
	showModal,
	customerId,
	useLocationFilter = false,
	addressType,
	locationFilter,
}: UseAddressBookDataProps) {
	const { isInternal } = useDeploymentType();

	const queryParams: AddressBookQueryParams = useMemo(
		() => ({
			customerId,
		}),
		[customerId],
	);

	// Build filtered query params for the location-filtered (shipment details) view.
	const filteredQueryParams: AddressBookFilteredQueryParams | null =
		useMemo(() => {
			if (!useLocationFilter || !addressType || !locationFilter) {
				return null;
			}
			if (!locationFilter.postalCode || !locationFilter.city) {
				return null;
			}
			return {
				customerId,
				postalCode: locationFilter.postalCode,
				city: locationFilter.city,
				state: locationFilter.state ?? "",
				country: locationFilter.country ?? "",
				stopType: addressType === "pickup" ? "shipper" : "receiver",
			};
		}, [useLocationFilter, addressType, locationFilter, customerId]);

	// Use filtered hook for shipment details, unfiltered for quote details
	const {
		data: unfilteredItems,
		isLoading: unfilteredLoading,
		isFetching: unfilteredFetching,
	} = useAddressBookItems(
		queryParams,
		showModal && (!useLocationFilter || !filteredQueryParams),
		isInternal,
	);

	const {
		data: filteredItems,
		isLoading: filteredLoading,
		isFetching: filteredFetching,
	} = useAddressBookItemsFiltered(
		filteredQueryParams,
		showModal && useLocationFilter && !!filteredQueryParams,
		isInternal,
	);

	const addressBookItems =
		useLocationFilter && filteredQueryParams ? filteredItems : unfilteredItems;
	const isLoading =
		useLocationFilter && filteredQueryParams
			? filteredLoading
			: unfilteredLoading;
	const isFetching =
		useLocationFilter && filteredQueryParams
			? filteredFetching
			: unfilteredFetching;

	const [data, setData] = useState<AddressBookItemSummary[]>([]);
	const [isDataLoading, setIsDataLoading] = useState(false);

	useEffect(() => {
		setData(addressBookItems ?? []);
	}, [addressBookItems]);

	useEffect(() => {
		setIsDataLoading(Boolean(isLoading || isFetching));
	}, [isFetching, isLoading]);

	return {
		data,
		setData,
		isLoading: isDataLoading,
		setIsLoading: setIsDataLoading,
		filteredQueryParams,
	};
}
